if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.setupExhaustTipAddon();
        this.setupTuningHardwareAddon();
      }

      setupExhaustTipAddon() {
        this.tipAddon = this.form.querySelector('[data-alpine-tip-addon]');
        if (!this.tipAddon) return;

        this.tipSelect = this.form.querySelector('[data-alpine-tip-select]');
        this.tipHelp = this.form.querySelector('[data-alpine-tip-help]');
        const mapNode = this.form.querySelector('[data-alpine-exhaust-size-map]');
        try {
          this.exhaustSizeMap = JSON.parse(mapNode?.textContent || '{}');
        } catch (_) {
          this.exhaustSizeMap = {};
        }

        this.tipScope = this.closest('.shopify-section') || this.parentElement || this;
        this.tipChangeHandler = () => this.scheduleExhaustTipRefresh();
        this.tipScope.addEventListener('change', this.tipChangeHandler);
        this.scheduleExhaustTipRefresh();
      }

      scheduleExhaustTipRefresh() {
        [0, 60, 180].forEach(delay => setTimeout(() => this.updateExhaustTipOptions(), delay));
      }

      updateExhaustTipOptions() {
        if (!this.tipAddon || !this.tipSelect) return;

        const size = String(this.exhaustSizeMap?.[String(this.variantIdInput?.value || '')] || '');
        let matching = 0;
        [...this.tipSelect.options].forEach((option, index) => {
          if (index === 0) return;
          const compatible = Boolean(size) && option.dataset.inlet === size;
          option.hidden = !compatible;
          option.disabled = !compatible;
          if (compatible) matching++;
        });

        const selected = this.tipSelect.selectedOptions?.[0];
        if (selected && selected.value && (selected.disabled || selected.hidden)) this.tipSelect.value = '';

        this.tipAddon.hidden = !size || matching === 0;
        if (this.tipHelp && size && matching) {
          this.tipHelp.textContent = 'Showing ' + size + '" inlet tips that match this exhaust. Choose one or leave No thanks.';
        }
      }

      setupTuningHardwareAddon() {
        this.sotfAddon = this.form.querySelector('[data-alpine-sotf-addon]');
        if (!this.sotfAddon) return;

        this.sotfSwitchSelect = this.form.querySelector('[data-alpine-sotf-switch]');
        this.sotfHarnessSelect = this.form.querySelector('[data-alpine-sotf-harness]');
        this.sotfHarnessWrap = this.form.querySelector('[data-alpine-sotf-harness-wrap]');
        this.sotfMountSelect = this.form.querySelector('[data-alpine-sotf-mount]');
        this.sotfMountWrap = this.form.querySelector('[data-alpine-sotf-mount-wrap]');
        this.sotfHelp = this.form.querySelector('[data-alpine-sotf-help]');

        try {
          this.currentTune = JSON.parse(this.form.querySelector('[data-alpine-current-tune]')?.textContent || '{}');
        } catch (_) {
          this.currentTune = {};
        }
        try {
          this.sotfProducts = JSON.parse(this.form.querySelector('[data-alpine-sotf-feed]')?.textContent || '[]');
        } catch (_) {
          this.sotfProducts = [];
        }

        this.sotfScope = this.closest('.shopify-section') || this.parentElement || this;
        this.sotfChangeHandler = () => setTimeout(() => this.updateTuningHardwareOptions(), 0);
        this.sotfScope.addEventListener('change', this.sotfChangeHandler);
        setTimeout(() => this.updateTuningHardwareOptions(), 0);
      }

      isSwitchableTune(text) {
        return /(\bSOTF\b|\bCSP[- ]?\d*\b|\bDSP\b|switch on the fly|\b5\s*tunes?\b|\b5[- ]tune\b)/i.test(String(text || ''));
      }

      tuningPlatform(text) {
        const value = String(text || '').toLowerCase();
        if (value.includes('commander')) return 'commander';
        if (value.includes('sct')) return 'sct';
        if (value.includes('ez lynk') || value.includes('ezlynk')) return 'ezlynk';
        if (value.includes('efi live') || value.includes('efilive')) return 'efilive';
        if (value.includes('mpvi3') || value.includes('hp tuner')) return 'mpvi3';
        if (value.includes('mm3')) return 'mm3';
        return '';
      }

      accessoryPlatformCompatible(tuneText, accessoryText) {
        const tunePlatform = this.tuningPlatform(tuneText);
        if (!tunePlatform) return true;

        const accessory = String(accessoryText || '').toLowerCase();
        const explicit = [];
        if (accessory.includes('commander')) explicit.push('commander');
        if (accessory.includes('sct')) explicit.push('sct');
        if (accessory.includes('ez lynk') || accessory.includes('ezlynk')) explicit.push('ezlynk');
        if (accessory.includes('efi live') || accessory.includes('efilive')) explicit.push('efilive');
        if (accessory.includes('mpvi3') || accessory.includes('hp tuner')) explicit.push('mpvi3');
        if (accessory.includes('mm3')) explicit.push('mm3');

        if (!explicit.length) return true;
        if (explicit.includes(tunePlatform)) return true;
        if (tunePlatform === 'mpvi3' && (explicit.includes('efilive') || accessory.includes('csp'))) return true;
        if (tunePlatform === 'efilive' && (explicit.includes('mpvi3') || accessory.includes('csp'))) return true;
        if (tunePlatform === 'mm3' && accessory.includes('csp')) return true;
        return false;
      }

      fitmentOverlap(a, b) {
        if (!a || !b) return false;
        const yearsOverlap = Number(a.from) <= Number(b.to) && Number(b.from) <= Number(a.to);
        return yearsOverlap &&
          String(a.make || '') === String(b.make || '') &&
          String(a.model || '') === String(b.model || '') &&
          String(a.engine || '') === String(b.engine || '');
      }

      accessoryVariantMatches(product, variant, tuneApplications, tuneText) {
        const apps = Array.isArray(variant?.applications) && variant.applications.length
          ? variant.applications
          : (Array.isArray(product?.applications) ? product.applications : []);
        const descriptor = String(product?.title || '') + ' ' + String(variant?.title || '');
        const universal = !apps.length &&
          /\buniversal\b/i.test(descriptor) &&
          !/(powerstroke|duramax|cummins|ecodiesel|eco diesel|ford|ram|dodge|nissan|jeep|gm\b)/i.test(descriptor);
        const fitmentMatch = universal || (apps.length && tuneApplications.some(tuneApp => apps.some(app => this.fitmentOverlap(tuneApp, app))));
        return Boolean(fitmentMatch) && this.accessoryPlatformCompatible(tuneText, String(product?.title || '') + ' ' + String(variant?.title || ''));
      }

      buildAccessoryChoices(kind, tuneApplications, tuneText) {
        const exact = [];
        const fallback = [];
        const tunePlatform = this.tuningPlatform(tuneText);

        (this.sotfProducts || []).forEach(product => {
          if (kind === 'mount' && product.kind !== 'mount') return;
          if (kind === 'harness' && product.kind !== 'harness') return;
          if (kind === 'switch' && product.kind !== 'switch') return;

          (product.variants || []).forEach(variant => {
            if (!this.accessoryVariantMatches(product, variant, tuneApplications, tuneText)) return;

            const text = String(product.title || '') + ' ' + String(variant.title || '');
            const item = {
              id: String(variant.id),
              label: product.title + (variant.title && variant.title !== 'Default Title' ? ' — ' + variant.title : '') + ' — ' + variant.price,
              product,
              variant,
              platformScore: this.tuningPlatform(text) === tunePlatform && tunePlatform ? 0 : 1,
            };

            const apps = Array.isArray(variant?.applications) && variant.applications.length
              ? variant.applications
              : (Array.isArray(product?.applications) ? product.applications : []);
            const descriptor = text;
            const isFallback = !apps.length &&
              /\buniversal\b/i.test(descriptor) &&
              !/(powerstroke|duramax|cummins|ecodiesel|eco diesel|ford|ram|dodge|nissan|jeep|gm\b)/i.test(descriptor);

            (isFallback ? fallback : exact).push(item);
          });
        });

        exact.sort((a,b) => a.platformScore - b.platformScore || a.label.localeCompare(b.label));
        fallback.sort((a,b) => a.label.localeCompare(b.label));
        return [...exact, ...fallback];
      }

      updateTuningHardwareOptions() {
        if (!this.sotfAddon || !this.sotfSwitchSelect) return;

        const variantId = String(this.variantIdInput?.value || '');
        const variantTitle = String(this.currentTune?.variants?.[variantId] || '');
        const tuneText = String(this.currentTune?.title || '') + ' ' + variantTitle;
        const switchable = this.isSwitchableTune(tuneText);
        const tuneApplications = Array.isArray(this.currentTune?.applications) ? this.currentTune.applications : [];

        this.sotfSwitchSelect.replaceChildren(new Option('Select required switch',''));
        if (this.sotfMountSelect) this.sotfMountSelect.replaceChildren(new Option('No thanks',''));

        if (!switchable) {
          this.sotfAddon.hidden = true;
          this.sotfRequired = false;
          return;
        }

        const switches = this.buildAccessoryChoices('switch', tuneApplications, tuneText);
        const harnesses = this.buildAccessoryChoices('harness', tuneApplications, tuneText);
        const mounts = this.buildAccessoryChoices('mount', tuneApplications, tuneText);

        switches.forEach(item => this.sotfSwitchSelect.add(new Option(item.label,item.id)));

        if (this.sotfHarnessSelect) {
          this.sotfHarnessSelect.replaceChildren(new Option('No thanks',''));
          harnesses.forEach(item => this.sotfHarnessSelect.add(new Option(item.label,item.id)));
        }
        if (this.sotfMountSelect) {
          this.sotfMountSelect.replaceChildren(new Option('No thanks',''));
          mounts.forEach(item => this.sotfMountSelect.add(new Option(item.label,item.id)));
        }

        this.sotfRequired = switches.length > 0;
        this.sotfAddon.hidden = false;
        this.sotfSwitchSelect.disabled = !switches.length;

        if (this.sotfHarnessSelect && this.sotfHarnessWrap) {
          const showHarness = harnesses.length > 0;
          this.sotfHarnessSelect.hidden = !showHarness;
          this.sotfHarnessWrap.hidden = !showHarness;
          this.sotfHarnessSelect.disabled = !showHarness;
        }

        if (this.sotfMountSelect && this.sotfMountWrap) {
          const showMount = mounts.length > 0;
          this.sotfMountSelect.hidden = !showMount;
          this.sotfMountWrap.hidden = !showMount;
          this.sotfMountSelect.disabled = !showMount;
        }

        if (switches.length === 1) this.sotfSwitchSelect.value = switches[0].id;

        if (this.sotfHelp) {
          this.sotfHelp.textContent = switches.length
            ? 'This tune option uses switch-on-the-fly hardware. Select the required switch. Matching harness/cable and mounting bracket options are shown separately when applicable.'
            : 'This is a switchable tune option, but no exact switch is mapped for this application. Alpine Diesel will confirm the required hardware after order.';
        }
      }

      disconnectedCallback() {
        if (this.tipScope && this.tipChangeHandler) this.tipScope.removeEventListener('change', this.tipChangeHandler);
        if (this.sotfScope && this.sotfChangeHandler) this.sotfScope.removeEventListener('change', this.sotfChangeHandler);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (this.sotfRequired && this.sotfSwitchSelect && !this.sotfSwitchSelect.value) {
          this.handleErrorMessage('Select the required SOTF switch for this tune option.');
          this.sotfSwitchSelect.focus();
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            let cartResponse = response;
            let addonError = '';
            const quantity = Math.max(1, Number(formData.get('quantity') || 1) || 1);
            const addonVariantIds = [
              this.tipSelect?.value,
              this.sotfSwitchSelect?.value,
              this.sotfHarnessSelect?.value,
              this.sotfMountSelect?.value,
            ].filter(Boolean);

            if (addonVariantIds.length) {
              const addonPayload = {
                items: addonVariantIds.map((id) => ({ id: Number(id), quantity })),
              };

              if (this.cart) {
                addonPayload.sections = this.cart.getSectionsToRender().map((section) => section.id);
                addonPayload.sections_url = window.location.pathname;
              }

              try {
                const addonResponse = await fetch(`${routes.cart_add_url}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                  },
                  body: JSON.stringify(addonPayload),
                });
                const addonBody = await addonResponse.json();
                if (!addonResponse.ok || addonBody.status) {
                  addonError = 'The main item was added, but one or more selected accessories could not be added. Please review the cart.';
                } else {
                  cartResponse = addonBody;
                }
              } catch (_) {
                addonError = 'The main item was added, but one or more selected accessories could not be added. Please review the cart.';
              }
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: cartResponse,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(cartResponse);
                    if (addonError) this.handleErrorMessage(addonError);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(cartResponse);
              if (addonError) this.handleErrorMessage(addonError);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
